import type { PublicState } from '../../shared/protocol';
import { babelLang, bt } from '../../shared/babel/strings';
import { formatArea, formatCountdown, pressureColor } from '../util/format';
import { useServerClock } from '../util/clock';

/** The Cockpit (§11.2): two pressure channels in one row. Time elapsed (left)
 *  and zone area remaining (right) each escalate Bone→Hazard→Moss→Signal so an
 *  asymmetric "zone racing ahead of time" reads at a glance. Digits show the
 *  human value; bars fill 0→100% as pressure rises. */
export function Cockpit({ state }: { state: PublicState }) {
  const { timer } = state;
  // v17 BOLT-074: the eyebrows were the two words Babel missed on Jake's prod
  // look. The VALUES stay latin (clock digits, area digits + unit symbol).
  const lang = babelLang(state);
  const cap = timer.capMs || 1;
  const roundEndsAt = timer.roundStartedAt ? timer.roundStartedAt + cap : null;
  const now = useServerClock(timer.serverNow, [roundEndsAt]);
  const elapsed = timer.roundStartedAt ? Math.max(0, now() - timer.roundStartedAt) : timer.elapsedMs;
  const timeP = Math.min(1, elapsed / cap);
  const remaining = Math.max(0, cap - elapsed);

  const zoneP = Math.min(1, Math.max(0, 1 - state.pctRemaining / 100));
  const a = formatArea(state.zoneAreaSqMeters);

  const isSeries = state.series.totalRounds > 1;

  return (
    <div className="cockpit" data-testid="cockpit">
      {isSeries && (
        <div className="ck-round" data-testid="ck-round">
          R{state.series.roundIndex + 1}/{state.series.totalRounds}
        </div>
      )}
      <div className="ck-half left" style={{ ['--hue' as string]: pressureColor(timeP) }}>
        <span className="ck-label">{bt(lang, 'time')}</span>
        <div className="ck-bar">
          <div className="ck-fill" style={{ width: `${timeP * 100}%` }} />
        </div>
        <span className="ck-val" data-testid="ck-time">
          {formatCountdown(remaining)}
        </span>
      </div>
      <div className="ck-div" />
      <div className="ck-half right" style={{ ['--hue' as string]: pressureColor(zoneP) }}>
        <span className="ck-label">{bt(lang, 'zone')}</span>
        <div className="ck-bar">
          <div className="ck-fill" style={{ width: `${zoneP * 100}%` }} />
        </div>
        <span className="ck-val" data-testid="ck-zone">
          {a.value}
          <span className="ck-unit">{a.unit}</span>
        </span>
      </div>
    </div>
  );
}
